import React from "react";

import { calculateMaxEstimatedCost } from "ComparePlans/calculateCost";
import PropTypes from "prop-types";
import { DetailSection } from "QuotePage/app/MobilePlanDetails";
import formatCurrency from "Utils/formatCurrency";
import getTooltip from "Utils/getTooltip";

import { Popover } from "Cobalt/Popover";
import { Table, TR, TD, TH } from "Cobalt/TableV2";
import { Tab, Tabs } from "Cobalt/Tabs";
import BodyText from "Common/BodyText";
import { Card, CardHeader, CardSection } from "Common/Card/";
import ColoredDot from "Common/ColoredDot";
import Icon from "Common/Icon";
import SIOverlayTrigger from "Common/SIOverlayTrigger";

import { finePrintLabels, finePrintSections } from "./finePrintSections";

import type { TableConfig } from "./finePrintSections";
import type { MedicalPlan } from "Utils/types";

type Props = {
  disableTitle: boolean;
  plan: MedicalPlan;
  isMobile: boolean;
  planDeviationProps: Record<string, any>;
  showColoredDots?: boolean;
};

export default class FinePrint extends React.Component<Props> {
  static defaultProps = {
    disableTitle: false,
    showColoredDots: true,
  };

  render() {
    const { plan, isMobile } = this.props;

    if (isMobile) {
      return (
        <>
          {finePrintLabels.map(([key, label]) => (
            <DetailSection title={label} key={key}>
              {this.createTableSection(key === "prescriptionDrugs" ? this.prescriptionDrugs() : finePrintSections[key])}
            </DetailSection>
          ))}
        </>
      );
    }
    return (
      <Card theme="flat" id="finePrint" p={0}>
        {this.renderTitle()}
        <CardSection>
          <Tabs defaultActiveKey={0} id={`fine-print-tabs-${plan.id}`}>
            {finePrintLabels.map(([key, label], i) => (
              <Tab key={i} eventKey={i} title={label}>
                {this.createTableSection(key === "prescriptionDrugs" ? this.prescriptionDrugs() : finePrintSections[key])}
              </Tab>
            ))}
          </Tabs>
        </CardSection>
      </Card>
    );
  }

  renderTitle() {
    const { disableTitle, showColoredDots } = this.props;

    if (!disableTitle) {
      return (
        <CardHeader>
          {showColoredDots && (
            <BodyText size="ns-twelve" componentClass="span" className="cost-legend float-right">
              (<span>Estimated Out of Pocket Cost:</span>
              <div className="ColoredDot ColoredDot--orange" />
              <span> Higher</span>
              <div className="ColoredDot ColoredDot--yellow" />
              <span> Moderate</span>
              <div className="ColoredDot ColoredDot--green" />
              <span> Lower</span>)
            </BodyText>
          )}
          Plan Details
        </CardHeader>
      );
    }
  }

  createTableSection(tableConfiguration: Array<TableConfig>) {
    const { plan, isMobile } = this.props;
    let head;
    const body = [];

    const labelSIOverlayTrigger = (label, props) => {
      const tooltip = getTooltip(label);

      if (tooltip) {
        const overlay = (
          <Popover showBackdrop={false} className="plan-term-tooltip" id={label}>
            {tooltip}
          </Popover>
        );
        return (
          <SIOverlayTrigger overlay={overlay} trackType="dashed-plan-term" trackId={label} {...props}>
            <span className="plan-term-tooltip-text">{label}</span>
          </SIOverlayTrigger>
        );
      }
      return <span>{label}</span>;
    };

    if (isMobile) {
      head = (
        <TR highlightFirstCell={false}>
          <TH>Plan Term</TH>
          <TH>Cost</TH>
          <TH>Details</TH>
        </TR>
      );
    } else {
      head = (
        <TR highlightFirstCell={false}>
          <TH>Service</TH>
          <TH>Expected Cost</TH>
          <TH
            style={{
              width: "60px",
            }}
          >
            {labelSIOverlayTrigger("Fixed Copay", {
              placement: "top",
            })}
          </TH>
          <TH
            style={{
              width: "30px",
            }}
          >
            or
          </TH>
          <TH
            style={{
              width: "92px",
            }}
          >
            {labelSIOverlayTrigger("Subject to Deductible", {
              placement: "top",
            })}
          </TH>
          <TH
            style={{
              width: "40%",
            }}
          >
            Details
          </TH>
        </TR>
      );
    }

    tableConfiguration.map((config, i) => {
      // Not all plan values have tooltips
      let planValue;

      if (plan.fine_print_updated[config.key][0] == "Click for full explanation.") {
        planValue = <span>{this.renderSBCLink()}</span>;
      } else if (["hospitalization", "labor_facility", "surgery"].includes(config.key)) {
        planValue = this.renderHospitalAndStaffTerm(config.key);
      } else {
        planValue = (
          <span>
            {plan.fine_print_updated[config.key][0]}
            {plan.fine_print_updated[config.key][1]}
          </span>
        );
      }

      const estimatedCost = this.renderCostCell(config.key);
      const deductibleRequired = this.checkDeductibleRequired(config.key);

      if (isMobile) {
        body.push(
          <TR key={i}>
            <TD>{config.label}</TD>
            <TD>{estimatedCost}</TD>
            <TD>{planValue}</TD>
          </TR>,
        );
      } else {
        body.push(
          <TR key={i}>
            <TD>{config.label}</TD>
            <TD>{estimatedCost}</TD>
            <TD
              style={{
                textAlign: "center",
              }}
            >
              {deductibleRequired ? null : this.renderCheck()}
            </TD>
            <TD />
            <TD
              style={{
                textAlign: "center",
              }}
            >
              {deductibleRequired ? this.renderCheck() : null}
            </TD>
            <TD>{planValue}</TD>
          </TR>,
        );
      }
    });
    return (
      <Table mt={4}>
        <thead>{head}</thead>
        <tbody>{body}</tbody>
      </Table>
    );
  }

  renderHospitalAndStaffTerm(hospitalKey: string) {
    const { plan } = this.props;
    const staffKeys = {
      hospitalization: "hospitalization_physician",
      labor_facility: "labor",
      surgery: "surgery_staff",
    };
    const hospitalLabels = {
      hospitalization: "Hospital",
      labor_facility: "Hospital room cost",
      surgery: "Facility",
    };
    const staffLabels = {
      hospitalization_physician: "Doctor's Fees",
      labor: "Hospital delivery cost",
      surgery_staff: "Surgeon",
    };
    const staffKey = staffKeys[hospitalKey];

    if (plan.fine_print_updated[hospitalKey][0] == plan.fine_print_updated[staffKey][0]) {
      return (
        <span>
          {plan.fine_print_updated[hospitalKey][0]}
          {plan.fine_print_updated[hospitalKey][1]}
        </span>
      );
    }
    return (
      <>
        <span>
          {hospitalLabels[hospitalKey]}: {plan.fine_print_updated[hospitalKey][0]} {plan.fine_print_updated[hospitalKey][1]}
        </span>
        <br />
        <span>
          {staffLabels[staffKey]}: {plan.fine_print_updated[staffKey][0]} {plan.fine_print_updated[staffKey][1]}
        </span>
      </>
    );
  }

  checkDeductibleRequired = (key: string) => {
    const { plan } = this.props;

    if (typeof plan.deductible_required[key] === "boolean") {
      if (key == "hospitalization") {
        return plan.deductible_required[key] || plan.deductible_required.hospitalization_physician;
      }
      if (key == "surgery") {
        return plan.deductible_required[key] || plan.deductible_required.surgery_staff;
      }
      if (key == "labor_facility") {
        return plan.deductible_required[key] || plan.deductible_required.labor;
      }

      return plan.deductible_required[key];
    }
    return true;
  };

  renderCheck() {
    return (
      <div>
        <Icon icon="check" className="plan-fine-print-check-icon" />
      </div>
    );
  }

  renderSBCLink() {
    const { plan, isMobile } = this.props;
    const text = isMobile ? "Tap for full explanation." : "Click for full explanation.";

    if (plan.has_sbc) {
      return (
        <a target="_blank" rel="nofollow noreferrer" href={plan.sbc_url}>
          {text}
        </a>
      );
    }
    const body = `Hello SimplyInsured,\n\nPlease send me a copy of the Summary of Benefits and Coverage for the ${plan.carrier} ${plan.display_name} Plan.\n\nThank you!\n\n\nPlan ID: ${plan.id}\nPlan Name: ${plan.raw_name}`;
    const mailto_url = `mailto:hello+sbc@simplyinsured.com?subject=SBC Request&body=${encodeURIComponent(body)}`;
    return (
      <a href={mailto_url} rel="nofollow">
        {text}
      </a>
    );
  }

  renderCostCell(serviceType: string) {
    const { plan, showColoredDots } = this.props;
    const devKey = /^drug_tier:/.test(serviceType) ? "brand_drugs" : serviceType;

    if (typeof plan.estimated_costs[serviceType] === "undefined") {
      return null;
    }

    return (
      <div
        style={{
          display: "flex",
        }}
      >
        {this.props.planDeviationProps ? (
          <ColoredDot
            value={plan.estimated_costs[serviceType]}
            mean={this.props.planDeviationProps[`mean_${devKey}`]}
            stdev={this.props.planDeviationProps[`stdev_${devKey}`]}
          />
        ) : plan.colored_dots && showColoredDots ? (
          <div className={`ColoredDot ColoredDot--${plan.colored_dots[serviceType]}`} />
        ) : null}
        <span
          style={{
            marginLeft: "10px",
            textAlign: "left",
          }}
        >
          {formatCurrency(calculateMaxEstimatedCost(plan, serviceType))}
        </span>
      </div>
    );
  }

  prescriptionDrugs(): Array<{
    label: string;
    key: string;
  }> {
    const { plan } = this.props;
    const drug_tier_names = plan.fine_print.drug_tiers ? plan.fine_print.drug_tiers.split("; ") : [];
    const drug_tiers = drug_tier_names.map((name) => ({
      label: name,
      key: `drug_tier:${name}`,
    }));
    return drug_tiers.concat([
      {
        label: "Drug Deductible",
        key: "separate_rx_deductible",
      },
    ]);
  }
}
FinePrint.propTypes = {
  plan: PropTypes.object,
};
